import React, { useContext, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Backdrop } from '@mui/material';
import { Analytics } from "@vercel/analytics/react";
import Tracker from '@openreplay/tracker';
import { AuthContext } from './context/AuthContext';
import Dashboard from './pages/Dashboard.jsx';
import Projects from './pages/Projects.jsx';
import Settings from './pages/Settings.jsx';
import Login from './pages/Login.jsx';
import ForgotPassword from './components/auth/ForgotPassword';
import AdminPanel from './pages/AdminPanel.jsx';
import Navbar from './components/NavBar.jsx';
import ProjectSettings from './pages/ProjectSettings';
import ResetPassword from './pages/ResetPassword';
import ProjectManagement from './pages/ProjectManagement';
import EmployeeManagement from './pages/EmployeeManagement';
import CreateProject from './pages/CreateProject';
import Library from './pages/Library.jsx';

const tracker = new Tracker({
  projectKey: "Vh5MHBpZikrkWbyBT9hx",
  capturePerformance: true,
  // Privacy settings to disable sanitization
  obscureTextEmails: false,      // Default: true
  obscureTextNumbers: false,     // Default: false (explicitly set)
  obscureInputEmails: false,     // Default: true
  obscureInputNumbers: false,    // Default: false (explicitly set)
  obscureInputDates: false,      // Default: false (explicitly set)
  defaultInputMode: 0,         // Default: 1 (obscured), set to 0 (plain)
});
tracker.start()

// Flag to ensure scripts are added only once
let helpScoutInitialized = false;

function App() {
  const location = useLocation();
  const { user, loading, roleLoading, role } = useContext(AuthContext);

  // Effect to load Help Scout scripts dynamically on mount
  useEffect(() => {
    if (helpScoutInitialized || window.Beacon) {
      return; // Scripts already loaded or loading
    }
    helpScoutInitialized = true; // Set flag immediately

    const script1 = document.createElement('script');
    script1.type = 'text/javascript';
    script1.async = true;
    // Using innerHTML for the complex function definition script
    script1.innerHTML = `!function(e,t,n){function a(){var e=t.getElementsByTagName("script")[0],n=t.createElement("script");n.type="text/javascript",n.async=!0,n.src="https://beacon-v2.helpscout.net",e.parentNode.insertBefore(n,e)}if(e.Beacon=n=function(t,n,a){e.Beacon.readyQueue.push({method:t,options:n,data:a})},n.readyQueue=[],"complete"===t.readyState)return a();e.attachEvent?e.attachEvent("onload",a):e.addEventListener("load",a,!1)}(window,document,window.Beacon||function(){});`;
    document.body.appendChild(script1);

    // The init script depends on the first one defining window.Beacon
    // We add it after the first one is appended, relying on browser execution order or async nature.
    // A more robust way might involve onload handlers, but let's try this first.
    const script2 = document.createElement('script');
    script2.type = 'text/javascript';
    script2.innerHTML = `window.Beacon('init', '9ffbb53e-de80-4583-851a-48c945399af0');`;
    // Append slightly later to increase chances of window.Beacon being ready
    setTimeout(() => document.body.appendChild(script2), 0);

  }, []); // Empty dependency array ensures this runs only once on mount

  // Identify user in OpenReplay when logged in
  useEffect(() => {
    if (user) {
      tracker.setUserID(user.email);
      tracker.setMetadata('name', user.user_metadata?.full_name || 'Unknown User');
      tracker.setMetadata('role', role || 'user');
    }
  }, [user, role]);

  // Open Help Scout Beacon when user logs in
  useEffect(() => {
    // Check for user and if Beacon function exists
    if (user && typeof window.Beacon === 'function') {      
      // Open the Beacon 
      window.Beacon('suggest');
    }
    // Maybe close Beacon if user logs out?
    // else if (!user && typeof window.Beacon === 'function') {
    //   window.Beacon('close'); 
    // }
  }, [user]); // Dependency on user only

  // Toon een laadindicator over het hele scherm terwijl de auth-status wordt geladen
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {user && <Navbar />}
      <Box component="main" sx={{ flexGrow: 1, pt: user ? 8 : 0 }}>
        <Routes>
          {/* Public Routes - Renderen alleen als NIET loading EN GEEN user */}
          <Route path="/login" element={!user ? <Login /> : <Navigate to="/" replace />} />
          {/* Reset Password pagina - toegankelijk via email link (impliciet geauthenticeerd) */}
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to="/login" replace />} />

          {/* Protected Routes - Renderen alleen als user bestaat (loading is al false hier) */}
          {user && (
            <>
              <Route path="/" element={<Dashboard />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/admin" element={<AdminPanel />} />
              <Route path="/employee-management" element={<EmployeeManagement />} />
              <Route path="/project-management" element={<ProjectManagement />} />
              <Route path="/projects/new" element={<CreateProject />} />
              <Route path="/projects/:id" element={<Projects />} />
              <Route path="/projects/:id/settings" element={<ProjectSettings />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/library" element={<Library />} />
              {/* Catch-all for logged-in users on unknown routes */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}

          {/* Fallback voor niet-ingelogde gebruikers (user is false, loading is al false) */}
          {!user && (
            <Route path="*" element={<Navigate to="/login" state={{ from: location }} replace />} />
          )}
        </Routes>
      </Box>
      <Analytics />
    </Box>
  );
}

export default App;